/* import __COLOCATED_TEMPLATE__ from './content-v2.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type { EntityType } from 'embercom/models/data/entity-types';
import type UnifiedContentStateService from 'embercom/services/unified-content-state';
import type KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';

interface Signature {
  Args: {
    finIsLive: boolean;
    openSectionId?: string;
    setOpenSectionId: (sectionId: string) => void;
    showPreview: boolean;
    showModal: (syncSourceType: string | undefined, entityType: EntityType) => Promise<void>;

    title?: string;
    onToggleSidebar?: () => void;
    onNewContent?: () => void;

    togglePreview?: () => void;
    toggleSidebar?: () => void;
    navbarCollapsed?: boolean;
    nativeContent?: boolean;
    listApi?: KnowledgeHubApi;
    showSyncedSourceBulkRowActions?: boolean;
    // Routes for folder navigation. Passed down from route templates
    // to keep components decoupled from router. Defaults to Knowledge Hub if not provided.
    parentFolderRoute?: string;
    allContentRoute?: string;
    demoType?: string;
  };
  Blocks: {
    default: [];
  };
}

/**
 * Wrapper component that conditionally renders the new Content V2 interface
 * when the feature flag is enabled, otherwise falls back to the existing content view.
 */
export default class ContentV2 extends Component<Signature> {
  @service declare appService: AppService;
  @service declare navbarCollapsingService: any;
  @service declare unifiedContentState: UnifiedContentStateService;

  /**
   * Determines if the new Content V2 interface should be shown
   * based on the feature flag
   */
  get shouldShowNewInterface(): boolean {
    return this.appService.app.canUseUnifiedContentUI;
  }

  /**
   * Determines if FilterableList should be used instead of UnifiedContent
   * when native_content=true
   */
  get shouldUseFilterableList(): boolean {
    if (!this.appService.app.canUseUnifiedContentUI || !this.args.listApi) {
      return false;
    }
    return this.args.nativeContent === true;
  }

  /**
   * Returns the listApi when it's guaranteed to be non-null
   * (i.e., when shouldUseFilterableList is true)
   */
  get listApi(): KnowledgeHubApi {
    // This getter should only be called when shouldUseFilterableList is true,
    // which guarantees that this.args.listApi is defined
    return this.args.listApi!;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::Knowledge::ContentV2': typeof ContentV2;
    'fin-ai-agent/knowledge/content-v2': typeof ContentV2;
  }
}
